import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Text from 'shopper/components/Text';

import Comments from 'components/Comments';
import { useCommentsRoot } from 'components/Comments/Root';
import StarsRating from 'components/StarsRating';

import placeholder from 'lib/placeholder';

import DomainProvider from 'providers/DomainProvider';

import CommentsAndReviewsTabs, {
  TAB_COMMENTS,
  TAB_REVIEWS,
} from '../CommentsAndReviewsTabs';
import CommentsList from '../CommentsList';
import ReviewsList from '../ReviewsList';

import COMMENT from 'constants/comment';
import OFFER from 'constants/offer';

/**
 * Limpa o `activeAnswer` do contexto quando o usuário sai da aba de
 * Comentários. Precisa estar DENTRO do `Comments.Root` pra acessar o
 * contexto — por isso é um componente separado em vez de um useEffect
 * em `OfferCommentsArea` (que renderiza o Root como filho).
 *
 * Sem essa limpeza, clicar "Responder" num comentário e depois trocar
 * pra aba Avaliações deixaria `activeAnswer` setado, e o review seria
 * enviado com o `parentId` do comentário respondido — enviando erroneamente
 * como reply de comentário ao invés de uma review nova.
 */
const ActiveAnswerClearer = ({ activeTab }) => {
  const { setActiveAnswer } = useCommentsRoot();

  useEffect(() => {
    if (activeTab !== TAB_COMMENTS) {
      setActiveAnswer(null);
    }
  }, [activeTab, setActiveAnswer]);

  return null;
};

const QUICK_COMMENTS = [
  {
    emojiLeft: '🚀',
    gaEventCategory: 'message_text_greatprice',
    text: 'Excelente Preço',
  },
  {
    emojiLeft: '🧐',
    gaEventCategory: 'message_text_specialists',
    text: 'Entendedores?',
  },
  {
    emojiLeft: '😢',
    gaEventCategory: 'message_text_icant',
    text: 'Não Consegui',
  },
  {
    emojiLeft: '🤩',
    gaEventCategory: 'message_text_ican',
    text: 'Consegui!',
  },
];

const OfferCommentsArea = ({
  aliasId = null,
  authors,
  authorCount,
  className,
  gaEventCategory,
  isOfferCommentsAllowed,
  offerCommentsCount,
  offerId,
  offerInstructions,
  offerPublished,
  offerStatusName,
  reviewRate = 0,
  reviews = [],
  reviewsListClassName,
  serverComments,
  totalReviews = 0,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_COMMENTS);
  const [reviewStars, setReviewStars] = useState(0);
  /*
    Backend nem sempre devolve `totalReviews` na resposta da oferta —
    em alguns casos vem 0 mesmo havendo reviews carregadas. ReviewsList
    propaga a contagem real via `onLoadedCountChange` e usamos esse
    valor pra o badge da aba "Avaliações", combinado com `totalReviews`
    e `reviews.length` (Math.max).
  */
  const [loadedReviewsCount, setLoadedReviewsCount] = useState(reviews.length);

  // Reseta a nota quando o usuário sai da aba Avaliações para não carregar
  // uma seleção antiga caso volte depois.
  useEffect(() => {
    if (activeTab !== TAB_REVIEWS) {
      setReviewStars(0);
    }
  }, [activeTab]);

  const handleReviewSubmitted = useCallback(() => setReviewStars(0), []);

  const isOfferAllowedToReceiveNewComments =
    isOfferCommentsAllowed && offerStatusName !== OFFER.STATUSES.WAITING;
  const isReviewsTab = activeTab === TAB_REVIEWS;

  /*
    Fallback do contador da aba: usamos o maior valor entre
    `totalReviews` (vindo da API), `reviews.length` (SSR) e
    `loadedReviewsCount` (lista que o ReviewsList efetivamente
    carregou via query). Cobre o caso de `totalReviews` vir 0 mesmo
    com reviews disponíveis.
  */
  const reviewsTabCount = Math.max(
    totalReviews,
    reviews.length,
    loadedReviewsCount
  );

  return (
    <div className={twMerge('flex min-h-0 flex-col', className)}>
      <DomainProvider domain={OFFER.DOMAIN} domainId={offerId}>
        <Comments.Root
          gaEventCategory={gaEventCategory}
          initialSort={COMMENT.SORT_VALUES.RELEVANCE}
        >
          <ActiveAnswerClearer activeTab={activeTab} />
          <CommentsAndReviewsTabs
            activeTab={activeTab}
            commentsCount={offerCommentsCount}
            commentsTabRightSlot={
              isOfferAllowedToReceiveNewComments ? <Comments.IconSort /> : null
            }
            reviewsCount={reviewsTabCount}
            onTabChange={setActiveTab}
          >
            <CommentsList
              aliasId={aliasId}
              authorCount={authorCount}
              authors={authors}
              isOfferCommentsAllowed={isOfferCommentsAllowed}
              offerCommentsCount={offerCommentsCount}
              offerId={offerId}
              offerInstructions={offerInstructions}
              offerPublished={offerPublished}
              offerStatusName={offerStatusName}
              serverComments={serverComments}
              userId={userId}
              userName={userName}
              userSpecialtyName={userSpecialtyName}
              userUsername={userUsername}
            />
            <ReviewsList
              aliasId={aliasId}
              averageStars={reviewRate}
              className={reviewsListClassName}
              initialReviews={reviews}
              totalReviews={totalReviews}
              onLoadedCountChange={setLoadedReviewsCount}
            />
          </CommentsAndReviewsTabs>
          <div className="shrink-0 pt-2">
            {isOfferAllowedToReceiveNewComments ? (
              <>
                {isReviewsTab && (
                  <div className="flex items-center gap-x-2 pb-2">
                    <Text as="span" size="size3">
                      Sua avaliação:
                    </Text>
                    <StarsRating
                      rating={reviewStars}
                      showFeedbackText={false}
                      size="size2"
                      userStarRating={reviewStars}
                      onRatingClick={setReviewStars}
                    />
                  </div>
                )}
                <Comments.TextEditor
                  commentStars={isReviewsTab ? reviewStars : undefined}
                  customPlaceholder={
                    isReviewsTab ? 'Deixe a sua avaliação' : undefined
                  }
                  gaEventCategory={gaEventCategory}
                  onCreateSuccess={
                    isReviewsTab ? handleReviewSubmitted : undefined
                  }
                >
                  {/*
                    QuickComments são atalhos de comentário de oferta
                    ("Excelente Preço", "Não Consegui", etc.) — não fazem
                    sentido como avaliação de produto. Escondidos na aba
                    Avaliações para evitar reviews de baixa qualidade.
                  */}
                  {!isReviewsTab && (
                    <Comments.QuickComments
                      className="min-h-[32px]"
                      messages={QUICK_COMMENTS}
                    />
                  )}
                </Comments.TextEditor>
              </>
            ) : (
              <Comments.ReadOnlyAlert
                message={
                  offerStatusName === OFFER.STATUSES.WAITING
                    ? placeholder(
                        'descriptions.offerWaitingToBeApprovedToAllowReceiveComments'
                      )
                    : placeholder(
                        'descriptions.offerDisallowedToReceiveComments'
                      )
                }
              />
            )}
          </div>
        </Comments.Root>
      </DomainProvider>
    </div>
  );
};

OfferCommentsArea.propTypes = {
  aliasId: PropTypes.number,
  authors: PropTypes.arrayOf(PropTypes.shape()),
  authorCount: PropTypes.number,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  isOfferCommentsAllowed: PropTypes.bool.isRequired,
  offerCommentsCount: PropTypes.number.isRequired,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerPublished: PropTypes.string,
  offerStatusName: PropTypes.string.isRequired,
  reviewRate: PropTypes.number,
  reviews: PropTypes.arrayOf(PropTypes.shape()),
  reviewsListClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  serverComments: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  totalReviews: PropTypes.number,
  userId: PropTypes.number,
  userName: PropTypes.string,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string,
};

export default OfferCommentsArea;
